import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaArrowLeft, FaEdit, FaTrash, FaFileAlt, FaClock, FaSpinner,
  FaMapMarkerAlt, FaBuilding, FaUser, FaPhone, FaEnvelope,
  FaExclamationTriangle
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import { quotes } from '../api';
import ConfirmDialog from '../components/ConfirmDialog';

const STATUS_LABELS = {
  quote_request: 'Quote Request', draft: 'Draft', sent: 'Sent', approved: 'Approved',
  rejected: 'Rejected', cancelled: 'Cancelled', client_accepted: 'Client Accepted',
  void: 'Voided'
};

const formatDuration = (ms) => {
  if (ms == null) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return '< 1m';
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) return `${hours}h ${remMins}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h ${remMins}m`;
};

function QuoteRequestDetailPage({ onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  // ✅ Custom in-app confirm modal (replaces window.confirm)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const loadQuote = async () => {
    setLoading(true);
    try {
      const res = await quotes.getOne(id);
      if (res.success) setQuote(res.quote);
      else toast.error(res.error || 'Quote not found');
    } catch { toast.error('Failed to load quote'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadQuote(); }, [id]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await quotes.delete(id);
      if (res.success) { toast.success('Quote request deleted'); navigate('/quotes/register'); }
      else toast.error(res.error || 'Failed to delete');
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(false); }
  };

  // DB stores UTC wall-clock in naive timestamp columns; parse as UTC so the
  // timer and displayed times are correct for SAST users (JS otherwise reads a
  // string without a timezone as local time, shifting it by -2h).
  const parseDbTs = (s) => (s ? new Date(typeof s === 'string' && !/Z|[+-]\d\d:\d\d$/.test(s) ? s + 'Z' : s) : null);
  const created = quote?.created_at ? parseDbTs(quote.created_at) : null;
  const sent = quote?.sent_at ? parseDbTs(quote.sent_at) : null;
  // The timer stops when the request stops being the office's problem: sent OR closed
  // (cancelled / rejected). Same rule as the register endpoint in backend/routes/quotes.js.
  const closed = quote?.cancelled_at ? parseDbTs(quote.cancelled_at)
    : (quote?.rejected_at ? parseDbTs(quote.rejected_at) : null);
  // Belt and braces, same as the register endpoint: a status that is already finished stops the
  // clock even when no closing stamp exists (e.g. a quote voided via the credit-note reversal).
  const CLOSED_STATUSES = ['cancelled', 'rejected', 'approved', 'client_accepted', 'void', 'credited'];
  const stopped = !!sent || !!closed || CLOSED_STATUSES.includes(String(quote?.status || '').toLowerCase());
  const pending = !stopped;
  const elapsedMs = created
    ? (sent ? Math.max(0, sent - created)
      : (closed ? Math.max(0, closed - created)
        : (pending ? Math.max(0, Date.now() - created) : null)))
    : null;

  const timerColor = () => {
    if (!pending) return 'text-green-600';
    if (elapsedMs < 6 * 3600000) return 'text-green';
    if (elapsedMs < 24 * 3600000) return 'text-yellow-600';
    if (elapsedMs < 48 * 3600000) return 'text-red-600';
    return 'text-red-800';
  };

  return (
    <AdminLayout activeTab="quoteregister" onLogout={onLogout} user={user}>
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate('/quotes/register')} className="mb-4 inline-flex items-center gap-2 text-gray-500 hover:text-navy">
          <FaArrowLeft /> Back to Register
        </button>

        {loading ? (
          <div className="flex items-center justify-center py-24"><FaSpinner className="animate-spin text-green text-3xl" /></div>
        ) : !quote ? (
          <div className="text-center py-24 text-gray-400">Quote request not found.</div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-navy">{quote.quote_number}</h1>
                <p className="text-sm text-gray-500 capitalize">{quote.quote_type} quote request</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => setShowDeleteConfirm(true)} disabled={deleting} className="px-4 py-2 bg-red-50 text-red-600 rounded-lg font-medium hover:bg-red-100 transition-colors disabled:opacity-50 flex items-center gap-2">
                  {deleting ? <FaSpinner className="animate-spin" /> : <FaTrash size={14} />} Delete
                </button>
                <button onClick={() => navigate(`/quotes/edit/${id}`)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center gap-2">
                  <FaEdit size={14} /> Edit
                </button>
                <button onClick={() => navigate(`/quotes/edit/${id}`)} className="px-4 py-2 bg-green text-white rounded-lg font-medium hover:bg-navy transition-colors flex items-center gap-2">
                  <FaFileAlt size={14} /> Create Quotation
                </button>
              </div>
            </div>

            {/* Timer */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 flex flex-wrap items-center gap-4">
              <FaClock className="text-green text-2xl" />
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Time since request</div>
                <div className={`text-2xl font-bold ${timerColor()}`}>{formatDuration(elapsedMs)}</div>
                {pending && elapsedMs > 48 * 3600000 && (
                  <div className="text-xs text-red-700 flex items-center gap-1"><FaExclamationTriangle /> Overdue — flagged</div>
                )}
              </div>
              <div className="ml-auto text-right">
                <div className="text-xs text-gray-500">Status</div>
                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">{STATUS_LABELS[quote.status] || quote.status}</span>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1"><FaBuilding className="text-green" /> Client</div>
                  <div className="font-semibold text-navy">{quote.customer_name || quote.customer?.company_name || '—'}</div>
                  {(quote.contact_person || quote.customer?.contact_person) && <div className="text-sm text-gray-500 flex items-center gap-1"><FaUser /> {quote.contact_person || quote.customer?.contact_person}</div>}
                  {(quote.contact_phone || quote.customer?.contact_phone) && <div className="text-sm text-gray-500 flex items-center gap-1"><FaPhone /> {quote.contact_phone || quote.customer?.contact_phone}</div>}
                  {(quote.contact_email || quote.customer?.contact_email) && <div className="text-sm text-gray-500 flex items-center gap-1"><FaEnvelope /> {quote.contact_email || quote.customer?.contact_email}</div>}
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Requested</div>
                  <div className="font-semibold text-navy">{created ? created.toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</div>
                  {sent && <div className="text-sm text-gray-500">Sent: {sent.toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>}
                  {pending && <div className="text-sm text-amber-600">Not sent yet — timer running</div>}
                  {!pending && !sent && closed && (
                    <div className="text-sm text-gray-500">
                      Timer stopped — {STATUS_LABELS[quote.status] || quote.status} on {closed.toLocaleString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Route</div>
                <div className="mt-1 space-y-1">
                  {quote.origin && <div className="flex items-center gap-2"><FaMapMarkerAlt className="text-green" /> {quote.origin}</div>}
                  {quote.destination && <div className="flex items-center gap-2"><FaMapMarkerAlt className="text-red-400" /> {quote.destination}</div>}
                  {!quote.origin && !quote.destination && <div className="text-sm text-gray-400">No location selected</div>}
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide">Client Request</div>
                <p className="mt-1 text-gray-700 whitespace-pre-wrap">{quote.description || '—'}</p>
              </div>

              {quote.client_price != null && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Current Price</div>
                  <div className="mt-1 text-lg font-bold text-navy">R {Number(quote.client_price).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                </div>
              )}

              <p className="text-xs text-gray-400 pt-2 border-t border-gray-100">
                Use <strong>Create Quotation</strong> to open the full quotation editor pre-filled with these details — add line items, pricing, terms, then Save &amp; Send. The timer stops the moment the quotation is sent.
              </p>
            </div>

          </>
        )}
      </div>

      {/* ✅ Custom in-app confirm modal (never window.confirm) */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Delete Quote Request"
        message="Delete this quote request? This cannot be undone."
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
      />
    </AdminLayout>
  );
}

export default QuoteRequestDetailPage;
